<script setup>
import Layout from "@/layouts/main.vue";
import PageHeader from "@/components/page-header";
import { onMounted, ref, computed } from 'vue';
import { VueDraggableNext } from 'vue-draggable-next';
import { useStore } from 'vuex';
import { truncate } from 'lodash';
import axios from 'axios';
import { authHeader } from './../../../helpers/authservice/auth-header';
import { required, helpers } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import moment from 'moment';
import Swal from 'sweetalert2';


const store = useStore();

const title = ref('Camera Locations');
const items = ref([
  {
    text: "Home"
  },
  {
    text: "Camera"
  },
  {
    text: "Locations",
    active: true
  }
]);

const loading = computed(() => store.state.globals.loading);

const cameraLocations = computed(({
    get() {
      return store.state.camera.cameraLocations;
    },
    set(value) {
      store.commit('camera/UPDATE_CAMERA_LOCATION_SORT', value);
    }
}));

const cameraLocation = ref({
  id: '',
  name: '',
  remarks: '',
  status: 'active',
  updated_at: null
})

const rules = {
    name: {
        required: helpers.withMessage("Camera Location Name is required", required),
    },
    remarks: {
        required: helpers.withMessage("Camera Location Remarks is required", required),
    }
}

const submitted = ref(false);

const v$ = useVuelidate(rules, cameraLocation);

const notification = computed(() => store.state.notification);

const standardDate = (date) => {
  if (!date) return 'N/A';
  return moment(date).format('YYYY-MM-DD h:mm A')
}

const getAllCameraLocations = async () => {
  await store.dispatch('camera/getCameraLocations')
}

const updateCameraLocationSort = () => {
  store.dispatch('camera/updateCameraLocationSort');
}

const getCameraLocationDetails = async (id) => {
    store.dispatch('globals/loading', null, { root: true });

    try {
        const { data } = await axios.get(`/camera_locations/${id}`, {
            headers: authHeader()
        });
        cameraLocation.value = data.data;
        store.dispatch('notification/clear', null, { root: true });
        store.dispatch('globals/loaded', null, { root: true });
    } catch (err) {
        console.log(err);
        store.dispatch('globals/loaded', null, { root: true });
    }
}

const updateCameraLocationDetails = async () => {
  try {
    submitted.value = true
    v$.value.$touch();
    if (v$.value.$invalid) return;
    await store.dispatch('camera/updateCameraLocation', cameraLocation.value)
  } catch (err) {
    console.log(err);
  }
}

  const deleteCameraLocation = (id, name) => {
      Swal.fire({
        title: "Are you sure?",
        text: "You won't be able to revert this!",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#34c38f",
        cancelButtonColor: "#f46a6a",
        confirmButtonText: "Delete",
      }).then(async (result) => {
        if (result.value) {
          await store.dispatch('camera/deleteCameraLocation', { id, name });
        }
      });
  }

onMounted(async () => {
  await getAllCameraLocations();
});

</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
        <div class="col lg-12">
          <b-overlay :show="loading">
            <div class="card">
               <div class="card-header">
                    <h4 class="card-title mb-0">Camera Locations</h4>
                     <small>Drag the camera location on the order you want to sort the queue in input and verify.</small>
                </div>
                <div class="card-body">
                   <div v-if="notification && notification.messages.length > 0">
                        <b-alert v-for="(message, index) in notification.messages" :key="index"
                            show variant="danger" class="fade show" role="alert">
                            {{ message }}
                        </b-alert>
                    </div>

                    <div class="text-left mb-3">
                      <router-link class="btn btn-primary" to="/camera/locations/create">
                        <i class="mdi mdi-camera-plus-outline mr-2"></i>
                        New Camera Location
                      </router-link>
                    </div>
                    
                    <div class="row">
                      <div class="col-md-12">
                        <b-list-group v-if="cameraLocations && cameraLocations.length > 0">
                          <VueDraggableNext 
                            v-model="cameraLocations"
                            @change="updateCameraLocationSort"
                          >
                            <transition-group
                              type="transition" 
                              name="flip-list"
                            >
                              <b-list-group-item 
                                v-for="item in cameraLocations"
                                :key="item.id"
                                tag="button"
                                class="list-group-item-action"
                                aria-current="true"
                              >
                                <div class="d-flex justify-content-between">
                                  <span>
                                    <i class="mdi mdi-drag-horizontal mr-3"></i>
                                  </span>
                                  <div class="w-25">
                                    {{ item.name}}
                                  </div>
                                  <div class="w-50" :title="item.remarks">{{ truncate(item.remarks, { length: 50 }) }}</div>
                                  <div clas="w-25">
                                    <b-badge :variant="item.status === 'active' ? 'success' : (item.status === 'archive' ? 'warning' : 'danger')">
                                    {{ item.status === 'active' ? 'Active' : (item.status === 'archive' ? 'Archived' : 'Inactive') }}</b-badge>
                                  </div>
                                  <div>
                                    <button 
                                      type="button" 
                                      title="Details" 
                                      class="btn btn-sm btn-info mr-1" 
                                      data-bs-toggle="modal"
                                      data-bs-target="#editCameraLocationModal"
                                      @click="getCameraLocationDetails(item.id)"
                                    >
                                      <i class="mdi mdi-eye"></i>
                                    </button>
                                     <button
                                          @click="deleteCameraLocation(item.id, item.name)"
                                          class="btn btn-sm btn-danger"
                                          title="Delete"
                                      >
                                        <i class="mdi mdi-delete"></i>
                                      </button>
                                  </div>
                                </div>
                              </b-list-group-item>
                            </transition-group>
                          </VueDraggableNext>
                        </b-list-group>
                        <div v-else>
                          <h4>No camera locations to sort</h4>
                        </div>
                    </div>
                  </div>
                </div>
            </div>
          </b-overlay>
        </div>
    </div>
  </Layout>
  <!-- Varying modal content -->
  <div class="modal fade" role="dialog" id="editCameraLocationModal" tabindex="-1" aria-labelledby="varyingcontentModalLabel" aria-hidden="true">
      <div class="modal-dialog">
          <div class="modal-content">
              <b-overlay :show="loading">
                <div class="modal-header">
                    <h5 class="modal-title" id="varyingcontentModalLabel">Camera Location Details</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                      <form>
                          <b-alert v-if="notification && notification.message"
                              show variant="success" class="fade show" role="alert">
                              {{ notification.message }}
                          </b-alert>

                          <div v-if="notification && notification.messages.length > 0">
                              <b-alert v-for="(message, index) in notification.messages" :key="index"
                                  show variant="danger" class="fade show" role="alert">
                                  {{ message }}
                              </b-alert>
                          </div>
                          <div class="mb-3">
                              <label for="recipient-name" class="col-form-label required">Name</label>
                              <input 
                                :class="{ 'is-invalid': submitted && v$.name.$error }"
                                type="text" class="form-control" id="recipient-name" v-model="cameraLocation.name">
                              <div
                                  v-for="item of v$.name.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="message-text" class="col-form-label required">Remarks</label>
                              <textarea 
                                :class="{ 'is-invalid': submitted && v$.remarks.$error }"
                                class="form-control" id="message-text" v-model="cameraLocation.remarks"></textarea>
                               <div
                                  v-for="item of v$.remarks.$errors"
                                  :key="item.$uid"
                                  class="invalid-feedback"
                              >
                                <span v-if="item.$message">{{ item.$message }}</span>
                              </div>
                          </div>
                          <div class="mb-3">
                              <label for="skip-ovr" class="form-label required">Status</label>
                               <select v-model="cameraLocation.status" class="form-select mb-3" aria-label="Camera Location Status Dropdown">
                                  <option value="active">Active</option>
                                  <option value="archive">Archived</option>
                                  <option value="inactive">Inactive</option>
                              </select>
                          </div>
                      </form>
                       <div class="text-muted">
                          Last Updated: {{ standardDate(cameraLocation.updated_at) }}
                        </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-danger" data-bs-dismiss="modal">
                      <i class="mdi mdi-close-thick"></i>
                      Close
                    </button>
                    <button @click.prevent="updateCameraLocationDetails" type="button" class="btn btn-success">
                      <i class="mdi mdi-content-save"></i>
                      Update Camera Location
                    </button>
                </div>
              </b-overlay>
          </div>
      </div>
    </div>
</template>